// import React from "react";
import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import ChatWindow from "./components/ChatWindow";
import AgentDashboard from "./components/AgentDashboard";
import TSECopilotDashboard from "./components/TSECopilotDashboard";
import TSEAgentRag from "./components/TSEAgentRag";
import TSEDashboard2 from "./components/TSEDashboard2";
import TseDashboard5, { genSessionId } from "./components/dashboard5/TseDashboard5";
import { NEW_SESSION_SEGMENT, NEW_SESSION_PATH } from "./components/dashboard5/sessionRoutes";
import AppLayout from "./components/AppLayout"; // <-- Adjust path as needed
import Login from "./components/Login";
import DiscoverPage from "./components/DiscoverPage";
import LangMemViewer from "./components/LangMemViewer";
import CacheBuster from "./components/CacheBuster";
import ApiKeysPage from "./components/ApiKeysPage";
import DocumentationPage from "./components/DocumentationPage";
import AdminPage from "./components/AdminPage";
import UserManagementPage from "./components/UserManagementPage";
import AdminApiKeysPage from "./components/AdminApiKeysPage";
import AdminSettingsPage from "./components/AdminSettingsPage";
import AdminTokenUsagePage from "./components/AdminTokenUsagePage";
import MyTrails from "./components/learning/MyTrails";
import TrailReview from "./components/learning/TrailReview";
import Corrections from "./components/learning/Corrections";
import RunbookGaps from "./components/learning/RunbookGaps";
import CatalogPage from "./components/learning/catalog/CatalogPage";
import InfraDashboard from "./components/InfraDashboard";
import AboutUs from "./components/AboutUs";
import { PrivacyPolicy, TermsOfService } from "./components/LegalPages";
import BatchMode from "./components/BatchMode";
import OpenCasesAnalysis from "./components/OpenCasesAnalysis";
import CaseAnalysisNextSteps from "./components/CaseAnalysisNextSteps";
import SourceMapPage from "./components/tse/SourceMapPage";
import CodeAgentPage from "./components/CodeAgentPage";
import BugTicketPage from "./components/bugs/BugTicketPage";
import CaseTicketPage from "./components/cases/CaseTicketPage";
import { AUTH_EXPIRED_EVENT } from "./utils/auth";

const AGENT_ROLES = new Set(["agent", "support_agent"]);

/**
 * Leave the SPA for the analyzer app.
 *
 * /analyzer/* is served by arta-analyzer through the API's proxy, not by this
 * bundle, so react-router must not try to resolve it: <Navigate> would match
 * the catch-all and bounce back here forever. A full document load is the
 * point.
 */
function AnalyzerRedirect({ to }) {
  useEffect(() => {
    window.location.replace(`/analyzer/${to}`);
  }, [to]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 text-sm text-gray-500">
      Opening the analyzer…
    </div>
  );
}

// Keyed by the route's session id so switching sessions (in-app navigate, not
// a hard reload) unmounts/remounts TseDashboard5 instead of reusing the old
// instance. TseDashboard5 seeds its session id into a ref once at mount
// (`sessionIdRef`), and without a key React Router keeps the same instance
// mounted across /TseDashboard5/<a> -> /TseDashboard5/<b>, leaving that ref
// pointed at the old session. useParams() only resolves against the matched
// Route, so this has to live in a small wrapper below <Routes>, not in
// AppContent itself.
//
// Nothing here mints a session id any more — the SERVER does, on the first
// thing the engineer actually does (tse:start). A blank chat lives at
// /TseDashboard5/new with no id at all, and the dashboard rewrites the address
// once the server names it.
//
// Which leaves this component one job, and it is the delicate one: DECIDE WHEN
// THE DASHBOARD IS A DIFFERENT DASHBOARD. The key has to change when the
// engineer opens another session, and must NOT change when a blank chat is
// given its id — that transition happens mid-turn, with a user bubble already
// in the transcript and an answer already streaming, and a remount there
// unmounts the subtree, throws the transcript away and drops the websocket.
//
// So the key is a mount token, not the path. `adoptedRef` is how this component
// learns that the id now in the URL is the one ITS OWN instance was just
// handed (onSessionIdAssigned, called before the dashboard navigates) rather
// than a different session someone clicked into.
function TseDashboard5Route({ onLogout, username }) {
  const { sessionId } = useParams();
  const isNew = sessionId === NEW_SESSION_SEGMENT;
  // Identifies the mounted instance. Opaque: never sent anywhere, never shown,
  // and emphatically not a session id — it exists only to be compared with
  // itself across renders.
  const keyRef = useRef(null);
  // Whether the mounted instance started life as a blank chat. Tracked apart
  // from the key because "we are already showing a blank chat" and "the key
  // happens to be a blank one" stop meaning the same thing the moment that
  // chat is named.
  const isBlankRef = useRef(false);
  // The id the mounted instance was assigned while it was still blank.
  const adoptedRef = useRef(null);
  // The session the mounted instance is CURRENTLY showing — which is not the
  // same thing as the key. After a close-and-continue the instance stays put
  // and starts showing the successor session, and the only way to notice later
  // that the engineer has clicked BACK to the closed parent is to have recorded
  // that move here. Comparing the parent's id against the key instead found it
  // equal (the key was minted when the parent mounted and never moved), decided
  // no remount was needed, and left the successor's transcript on screen under
  // the parent's URL.
  const shownRef = useRef(null);

  if (isNew) {
    // Arriving at /new asks for a fresh chat — unless one is already mounted,
    // in which case re-rendering (the dashboard nulls location.state on mount,
    // which lands back here) must not throw it away.
    if (!isBlankRef.current) {
      keyRef.current = `blank:${genSessionId()}`;
      isBlankRef.current = true;
      adoptedRef.current = null;
      // A blank chat shows no session at all. Leaving the previous one recorded
      // here would make clicking it in Recents look like "already on screen"
      // and skip the remount, keeping the empty chat mounted under its URL.
      shownRef.current = null;
    }
  } else if (sessionId) {
    if (adoptedRef.current === sessionId) {
      // Our own chat, just named by the server (a blank one being minted, or a
      // close-and-continue handing this instance the successor id). Same
      // instance, same key — the remount is exactly what must not happen here.
      // Only the bookkeeping moves: it is not blank any more, so the NEXT visit
      // to /new remounts, and it is now showing this session, so a later click
      // onto any OTHER one does.
      isBlankRef.current = false;
      shownRef.current = sessionId;
    } else if (shownRef.current !== sessionId) {
      // A different session than the one on screen: remount onto it. The token
      // is minted fresh rather than being the id itself, because React only
      // remounts when the key VALUE changes — and coming back to a session this
      // instance had already mounted once (parent -> successor -> parent) would
      // hand back the identical id and quietly skip the remount.
      keyRef.current = `sess:${genSessionId()}`;
      shownRef.current = sessionId;
      isBlankRef.current = false;
      adoptedRef.current = null;
    }
  }
  // The bare path is an alias for "start a new chat".
  if (!sessionId) {
    return <Navigate to={NEW_SESSION_PATH} replace />;
  }
  return (
    <TseDashboard5
      key={keyRef.current}
      sessionId={isNew ? null : sessionId}
      isNewSession={isNew}
      onSessionIdAssigned={(id) => { adoptedRef.current = id; }}
      onLogout={onLogout}
      username={username}
    />
  );
}

function AppContent() {
  const location = useLocation();
  const [auth, setAuth] = useState(() => {
    // Try to load from localStorage for persistence
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    const username = localStorage.getItem("username");
    const VALID_ROLES = new Set(["user", "agent", "support_agent", "admin"]);
    const isValid = token && token !== "undefined" && token !== "null"
      && role && VALID_ROLES.has(role)
      && username && username !== "undefined" && username !== "null";
    if (!isValid) {
      // Clear any stale / corrupted auth data
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("username");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("expires_at");
      return null;
    }
    return { token, role, username };
  });

  const handleLogin = (token, role, username, refreshToken = null, expiresAt = null) => {
    setAuth({ token, role, username });
    localStorage.setItem("token", token);
    localStorage.setItem("role", role);
    localStorage.setItem("username", username);
    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }
    if (expiresAt !== null && expiresAt !== undefined) {
      localStorage.setItem("expires_at", String(expiresAt));
    }
  };

  const handleLogout = () => {
    setAuth(null);
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("username");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("expires_at");
  };

  useEffect(() => {
    const onAuthExpired = () => {
      setAuth(null);
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("username");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("expires_at");
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
  }, []);

  // Cross-tab logout: when another tab clears the token from localStorage,
  // the browser fires a "storage" event here — use it to sync logout state.
  useEffect(() => {
    const onStorageChange = (e) => {
      if (e.key === "token" && !e.newValue) {
        // Another tab logged out — clean up this tab too
        setAuth(null);
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("username");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("expires_at");
      }
    };
    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
  }, []);

  const isAbout = location.pathname === "/about";

  if (isAbout) {
    return <AboutUs />;
  }
  if (location.pathname === "/privacy") {
    return <PrivacyPolicy />;
  }
  if (location.pathname === "/terms") {
    return <TermsOfService />;
  }

  return (
    <>
      {/* Signed in, the sidebar carries the version (beside the logo, or above
          Logout when collapsed), so the floating badge would be a second copy
          of the same string. Signed out there is no sidebar to carry it, and
          the build number is exactly what a support conversation opens with —
          so the badge stays for the login screen. */}
      {!auth && <CacheBuster position="right" />}
      <AppLayout>
        <Routes>
          {/* Login route */}
          <Route
            path="/"
            element={
              auth ? (
                auth.role === "admin"
                  ? <Navigate to="/admin/dashboard" />
                  : AGENT_ROLES.has(auth.role)
                      ? <Navigate to="/TseDashboard5" />
                      : <Navigate to="/ent-user" />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          {/* User chat route */}
          <Route
            path="/ai-agent"
            element={
              auth && auth.role === "user" ? (
                <ChatWindow username={auth.username} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Enterprise user chat route — simplified ChatWindow variant.
              The optional :sessionId is what makes an enterprise chat
              addressable, so a row in the rail's Recents can reopen it. Bare
              /ent-user is still the greeting screen (no session open), exactly
              as before — ChatWindow reads the param and arms the split view
              only when one is present. Same role gate either way. */}
          <Route
            path="/ent-user/:sessionId?"
            element={
              auth && auth.role === "user" ? (
                <ChatWindow username={auth.username} onLogout={handleLogout} variant="enterprise" />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Agent dashboard route */}
          <Route
            path="/agent"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <AgentDashboard onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* TSE Copilot Dashboard route */}
          <Route
            path="/TseDashboard"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <TSECopilotDashboard onLogout={handleLogout} username={auth.username} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* TSE Agent RAG route */}
          <Route
            path="/TseAgentRag"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <TSEAgentRag onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Search window — all authenticated users */}
          <Route
            path="/search"
            element={
              auth ? (
                <TSEAgentRag onLogout={handleLogout} standalone />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* TSE Dashboard 2 — REST-based, no sockets */}
          <Route
            path="/TseDashboard2"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <TSEDashboard2 onLogout={handleLogout} username={auth.username} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* TSE Dashboard 3 — Socket.IO streaming.
              The session id is part of the path so a session is addressable:
              history can link to one, and a reload returns to it. The bare route
              still works: TseDashboard5Route mints an id and redirects to the
              canonical URL before the dashboard mounts. */}
          <Route path="/TseDashboard3/:sessionId?" element={<LegacyDashboardRedirect />} />
          <Route
            path="/TseDashboard5/:sessionId?"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <TseDashboard5Route onLogout={handleLogout} username={auth.username} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Bug ticket detail — opened in a new tab from the Bugs panel and
              from bug links in Batch Mode answers. Batch Mode is open to every
              authenticated role (see /TSEBatchDashboard below) and the
              /api/bugs + /api/cases endpoints only require a signed-in user,
              so these pages follow the same gate. An AGENT_ROLES gate here
              bounced a role=user click through "/" to /ent-user. */}
          <Route
            path="/jira/:issueKey"
            element={auth ? <BugTicketPage /> : <Navigate to="/" />}
          />
          {/* SFDC case detail — opened in a new tab from the Cases panel */}
          <Route
            path="/case/:caseId"
            element={auth ? <CaseTicketPage /> : <Navigate to="/" />}
          />
          {/* Stored case urls use the plural form: …/cases/<id> */}
          <Route
            path="/cases/:caseId"
            element={auth ? <CaseTicketPage /> : <Navigate to="/" />}
          />
          {/* Code Agent — Claude Code diagnostic chat */}
          <Route
            path="/code-agent/:sessionId?"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <CodeAgentPage onLogout={handleLogout} username={auth.username} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Batch Mode — all authenticated users */}
          <Route
            path="/TSEBatchDashboard"
            element={
              auth ? (
                <BatchMode onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Open Cases Analysis — table of open cases (agents) */}
          <Route
            path="/open-cases"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <OpenCasesAnalysis onLogout={handleLogout} username={auth.username} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Case Analysis and Next Steps — opens in a new tab from the table */}
          <Route
            path="/case-analysis/:caseId"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <CaseAnalysisNextSteps onLogout={handleLogout} username={auth.username} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Source Diagnostic Map — opens in a new tab from a causal source card.
              Session-free route is canonical; the :sessionId form is kept so
              already-shared links do not 404 (the page ignores the id). */}
          <Route
            path="/source-map"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <SourceMapPage />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/source-map/:sessionId"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <SourceMapPage />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route path="/discover" element={<DiscoverPage />} />
          {/* Conversation-memory viewer: what LangMem holds per session. */}
          <Route path="/langmem" element={<LangMemViewer />} />
          <Route
            path="/documentation"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <DocumentationPage onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/api-keys"
            element={
              auth && AGENT_ROLES.has(auth.role) ? (
                <ApiKeysPage onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Admin page — pending signups */}
          <Route
            path="/admin"
            element={
              auth && auth.role === "admin" ? (
                <AdminPage onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Admin — user management */}
          <Route
            path="/admin/users"
            element={
              auth && auth.role === "admin" ? (
                <UserManagementPage onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Admin — API key management */}
          <Route
            path="/admin/api-keys"
            element={
              auth && auth.role === "admin" ? (
                <AdminApiKeysPage onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Admin — token usage & costs */}
          <Route
            path="/admin/token-usage"
            element={
              auth && auth.role === "admin" ? (
                <AdminTokenUsagePage onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Admin — settings */}
          <Route
            path="/admin/settings"
            element={
              auth && auth.role === "admin" ? (
                <AdminSettingsPage onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Learning module — review backlog and one trail.
              Open to TSEs as well as admins ON PURPOSE: the reviewer is the
              engineer who ran the session, and `result` (did this step work?)
              is a question only they can answer. Admin-only gating made the
              loop unrunnable — chat is gated to AGENT_ROLES, so an admin can
              never produce a trail and a TSE could never review one. */}
          <Route
            path="/learning"
            element={
              auth && (auth.role === "admin" || AGENT_ROLES.has(auth.role)) ? (
                <MyTrails onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/learning/corrections"
            element={
              auth && (auth.role === "admin" || AGENT_ROLES.has(auth.role)) ? (
                <Corrections onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Learning Catalog — the patterns engineers taught (learned on submit), to search, edit and undo. Open to admin and
              support agents for now; the role mapping comes with the separate RBAC. Must precede /:id. */}
          <Route
            path="/learning/catalog"
            element={
              auth && (auth.role === "admin" || AGENT_ROLES.has(auth.role)) ? (
                <CatalogPage onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* The SME review screen it replaced: old links land on the catalog. */}
          <Route path="/learning/review" element={<Navigate to="/learning/catalog" replace />} />
          {/* Runbook Gaps — admin-only insight over ALL engineers' data, so
              gated the same way as the other admin-only learning routes
              (learning_status, override_hotspots) rather than opened to
              AGENT_ROLES like the trail/correction routes above.
              ROUTE ORDER: this and /corrections must precede /:id so the
              param route doesn't swallow the fixed segments. */}
          <Route
            path="/learning/insights"
            element={
              auth && auth.role === "admin" ? (
                <RunbookGaps onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/learning/:id"
            element={
              auth && (auth.role === "admin" || AGENT_ROLES.has(auth.role)) ? (
                <TrailReview onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* Admin — infrastructure dashboard */}
          <Route
            path="/admin/dashboard"
            element={
              auth && auth.role === "admin" ? (
                <InfraDashboard onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          {/* The capture analyzers are a SEPARATE app now (arta-analyzer/),
              /analyze/pcap goes to pcap_legacy on purpose: it is the in-browser
              decoder those old links meant. /analyzer/pcap is now the
              sharkd-backed one, which uploads.
              proxied by the API at /analyzer/*. They are not React Router
              routes, so these have to leave the SPA rather than navigate
              inside it -- see AnalyzerRedirect below. */}
          <Route path="/analyze/har" element={<AnalyzerRedirect to="har" />} />
          <Route path="/analyze/pcap" element={<AnalyzerRedirect to="pcap_legacy" />} />
          <Route path="/analyze/pcapwireshark" element={<AnalyzerRedirect to="pcap" />} />
          <Route path="/analyze/webshark" element={<AnalyzerRedirect to="pcap" />} />
          <Route path="/analyzer/*" element={<AnalyzerRedirect to="" />} />

          {/* Safety net: Redirect old /chat route to new /ent-user route */}
          <Route path="/chat" element={<Navigate to="/ent-user" replace />} />
        </Routes>
      </AppLayout>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

// Bookmarks and shared links from before the 3 -> 5 rename.
function LegacyDashboardRedirect() {
  const { sessionId } = useParams();
  return <Navigate to={sessionId ? `/TseDashboard5/${sessionId}` : "/TseDashboard5"} replace />;
}

export default App;

